/**
 * Giveaway slugs.
 *
 * A giveaway is addressed at `/g/{slug}` — short, because these links are
 * pasted into Discord messages. The slug defaults to a
 * slugified version of the event name and can be overridden when the giveaway
 * is created; after that it is editable, because the link people share is more
 * useful than a numeric id and organisers rename events.
 */

export const SLUG_MIN_LENGTH = 3
export const SLUG_MAX_LENGTH = 64

/**
 * Name -> slug. Diacritics are folded rather than dropped, so "Pokémon Legends"
 * becomes "pokemon-legends" instead of "pok-mon-legends".
 */
export function slugify(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/g, '')
}

/** What the user typed, cleaned up enough to keep typing in. */
export function normalizeSlugInput(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .slice(0, SLUG_MAX_LENGTH)
}

export function slugError(slug: string): string | null {
  if (slug.length === 0) return 'Enter a link for your giveaway.'
  if (slug.length < SLUG_MIN_LENGTH) return `Links need at least ${SLUG_MIN_LENGTH} characters.`
  if (slug.length > SLUG_MAX_LENGTH) return `Links can be at most ${SLUG_MAX_LENGTH} characters.`
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
    return 'Use lowercase letters, numbers and single hyphens.'
  }
  // A purely numeric slug is ambiguous with a giveaway id on the same route.
  if (/^\d+$/.test(slug)) return 'Links cannot be only numbers.'
  return null
}

export const isValidSlug = (slug: string): boolean => slugError(slug) === null

/**
 * The short public prefix. `/giveaway/:idOrSlug` is kept as a redirect: it is
 * the path the Quasar app linked to, so old links have to keep working.
 */
export const GIVEAWAY_PATH_PREFIX = '/g'
export const LEGACY_GIVEAWAY_PATH_PREFIX = '/giveaway'

/** The public path for a giveaway. */
export const giveawayPath = (slug: string): string => `${GIVEAWAY_PATH_PREFIX}/${slug}`

/** The absolute URL to share, resolved against the current origin. */
export function giveawayUrl(slug: string, origin?: string): string {
  const base =
    origin ?? (typeof window === 'undefined' ? '' : window.location.origin.replace(/\/$/, ''))
  return `${base}${giveawayPath(slug)}`
}
